"use client"

import { useState, useEffect } from "react"

const DocumentEditModal = ({
  showModal,
  documents,
  newFiles,
  documentsToDelete,
  onClose,
  onSave,
  onAddMore,
  onMarkForDeletion,
  onRemoveNewFile,
  formatFileSize,
  isUploading,
}) => {
  const [editableDocuments, setEditableDocuments] = useState([])
  const [hasChanges, setHasChanges] = useState(false)

  useEffect(() => {
    if (documents) {
      setEditableDocuments(
        documents.map((doc, index) => ({
          ...doc,
          id: doc.id || index,
          originalFileName: doc.fileName,
        })),
      )
    }
  }, [documents])

  useEffect(() => {
    // Check if there are any changes
    const hasFileNameChanges = editableDocuments.some((doc) => doc.fileName !== doc.originalFileName)
    const hasNewFiles = newFiles && newFiles.length > 0
    const hasDeletedFiles = documentsToDelete && documentsToDelete.length > 0

    setHasChanges(hasFileNameChanges || hasNewFiles || hasDeletedFiles)
  }, [editableDocuments, newFiles, documentsToDelete])

  if (!showModal) return null

  const handleFileNameChange = (docId, newFileName) => {
    setEditableDocuments((prev) => prev.map((doc) => (doc.id === docId ? { ...doc, fileName: newFileName } : doc)))
  }

  const handleSave = () => {
    onSave(editableDocuments)
  }

  const renderDocumentPreview = (document) => {
    const isImage = document.fileType?.startsWith("image/") || document.url?.match(/\.(jpg|jpeg|png|gif)$/i)
    const isPdf = document.fileType === "application/pdf" || document.url?.toLowerCase().includes(".pdf")

    if (isPdf) {
      return (
        <div className="text-center">
          <i className="bi bi-file-earmark-pdf text-danger" style={{ fontSize: "3rem" }}></i>
          <p className="mt-2 mb-0">PDF Document</p>
        </div>
      )
    } else if (isImage) {
      return (
        <img
          src={document.url || "/placeholder.svg"}
          alt="Document Preview"
          className="img-fluid"
          style={{
            maxHeight: "120px",
            maxWidth: "100%",
            objectFit: "cover",
            border: "1px solid #dee2e6",
            borderRadius: "0.375rem",
          }}
          onError={(e) => {
            e.target.src = "/placeholder.svg?height=120&width=120"
          }}
        />
      )
    } else {
      return (
        <div className="text-center">
          <i className="bi bi-file-earmark text-secondary" style={{ fontSize: "3rem" }}></i>
          <p className="mt-2 mb-0">Document</p>
        </div>
      )
    }
  }

  const renderNewFilePreview = (file) => {
    const isImage = file.type?.startsWith("image/")
    const isPdf = file.type === "application/pdf"

    if (isPdf) {
      return (
        <div className="text-center">
          <i className="bi bi-file-earmark-pdf text-danger" style={{ fontSize: "3rem" }}></i>
          <p className="mt-2 mb-0">PDF Document</p>
        </div>
      )
    } else if (isImage) {
      return (
        <img
          src={URL.createObjectURL(file) || "/placeholder.svg"}
          alt="New File Preview"
          className="img-fluid"
          style={{
            maxHeight: "120px",
            maxWidth: "100%",
            objectFit: "cover",
            border: "1px solid #dee2e6",
            borderRadius: "0.375rem",
          }}
        />
      )
    } else {
      return (
        <div className="text-center">
          <i className="bi bi-file-earmark text-secondary" style={{ fontSize: "3rem" }}></i>
          <p className="mt-2 mb-0">Document</p>
        </div>
      )
    }
  }

  const totalDocuments = editableDocuments.length + (newFiles?.length || 0)
  const deletedCount = documentsToDelete?.length || 0

  return (
    <div
      className="modal fade show"
      style={{
        display: "block",
        backgroundColor: "rgba(0,0,0,0.5)",
        zIndex: 1055,
      }}
    >
      <div className="modal-dialog modal-xl">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title d-flex align-items-center">
              <i className="bi bi-pencil-square me-2"></i>
              Edit Documents
              <span className="badge bg-secondary ms-2">
                {totalDocuments} file{totalDocuments !== 1 ? "s" : ""}
              </span>
              {deletedCount > 0 && <span className="badge bg-danger ms-2">{deletedCount} to delete</span>}
            </h5>
            <button type="button" className="btn-close" onClick={onClose} aria-label="Close"></button>
          </div>

          <div className="modal-body">
            {totalDocuments === 0 ? (
              <div className="text-center py-4">
                <i className="bi bi-file-earmark-x text-muted" style={{ fontSize: "4rem" }}></i>
                <p className="text-muted mt-3">No documents to edit</p>
                <button className="btn btn-primary" onClick={onAddMore}>
                  <i className="bi bi-plus-circle me-2"></i>
                  Add Documents
                </button>
              </div>
            ) : (
              <div className="row g-3">
                {/* Existing Documents */}
                {editableDocuments.map((document, index) => {
                  const isMarkedForDeletion = documentsToDelete?.includes(document.id)
                  return (
                    <div key={document.id} className="col-md-6 col-lg-4">
                      <div className={`card h-100 ${isMarkedForDeletion ? "border-danger bg-light" : ""}`}>
                        <div className="card-body">
                          <div className="d-flex justify-content-between align-items-start mb-3">
                            <span className="badge bg-primary">#{index + 1}</span>
                            <div className="btn-group btn-group-sm">
                              <button
                                type="button"
                                className={`btn ${isMarkedForDeletion ? "btn-warning" : "btn-outline-danger"} btn-sm`}
                                onClick={() => onMarkForDeletion(document.id)}
                                title={isMarkedForDeletion ? "Restore Document" : "Mark for Deletion"}
                              >
                                <i
                                  className={`bi ${isMarkedForDeletion ? "bi-arrow-counterclockwise" : "bi-trash"}`}
                                ></i>
                              </button>
                            </div>
                          </div>

                          {isMarkedForDeletion && (
                            <div className="alert alert-danger alert-sm mb-3">
                              <i className="bi bi-exclamation-triangle me-1"></i>
                              Marked for deletion
                            </div>
                          )}

                          <div className="text-center mb-3">{renderDocumentPreview(document)}</div>

                          <div className="mb-3">
                            <label className="form-label small">File Name</label>
                            <input
                              type="text"
                              className="form-control form-control-sm"
                              value={document.fileName}
                              onChange={(e) => handleFileNameChange(document.id, e.target.value)}
                              placeholder="Enter file name"
                              disabled={isMarkedForDeletion}
                            />
                            {document.fileName !== document.originalFileName && !isMarkedForDeletion && (
                              <small className="text-warning">
                                <i className="bi bi-exclamation-triangle me-1"></i>
                                Name changed
                              </small>
                            )}
                          </div>

                          <div className="d-flex justify-content-between align-items-center">
                            <small className="text-muted">
                              {document.fileSize && formatFileSize ? (
                                <>
                                  <i className="bi bi-hdd me-1"></i>
                                  {formatFileSize(document.fileSize)}
                                </>
                              ) : (
                                "Unknown size"
                              )}
                            </small>
                            <div className="btn-group btn-group-sm">
                              <a
                                href={document.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="btn btn-outline-primary btn-sm"
                                title="Preview"
                              >
                                <i className="bi bi-eye"></i>
                              </a>
                              <a
                                href={document.url}
                                download={document.fileName}
                                className="btn btn-outline-success btn-sm"
                                title="Download"
                              >
                                <i className="bi bi-download"></i>
                              </a>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )
                })}

                {/* New Files to be Added */}
                {newFiles?.map((file, index) => (
                  <div key={`new-${index}`} className="col-md-6 col-lg-4">
                    <div className="card h-100 border-success">
                      <div className="card-body">
                        <div className="d-flex justify-content-between align-items-start mb-3">
                          <span className="badge bg-success">New #{index + 1}</span>
                          <button
                            type="button"
                            className="btn btn-outline-danger btn-sm"
                            onClick={() => onRemoveNewFile(index)}
                            title="Remove File"
                          >
                            <i className="bi bi-x"></i>
                          </button>
                        </div>

                        <div className="alert alert-success alert-sm mb-3">
                          <i className="bi bi-plus-circle me-1"></i>
                          New file to be added
                        </div>

                        <div className="text-center mb-3">{renderNewFilePreview(file)}</div>

                        <div className="mb-3">
                          <label className="form-label small">File Name</label>
                          <input
                            type="text"
                            className="form-control form-control-sm"
                            value={file.name}
                            readOnly
                            placeholder="File name"
                          />
                        </div>

                        <div className="d-flex justify-content-between align-items-center">
                          <small className="text-muted">
                            <i className="bi bi-hdd me-1"></i>
                            {formatFileSize ? formatFileSize(file.size) : "Unknown size"}
                          </small>
                          <small className="text-success">
                            <i className="bi bi-cloud-upload me-1"></i>
                            Ready to upload
                          </small>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}

                {/* Add More Documents Card */}
                <div className="col-md-6 col-lg-4">
                  <div className="card h-100 border-dashed">
                    <div className="card-body d-flex flex-column justify-content-center align-items-center text-center">
                      <i className="bi bi-plus-circle text-primary" style={{ fontSize: "3rem" }}></i>
                      <p className="mt-3 mb-3">Add more documents</p>
                      <button className="btn btn-outline-primary" onClick={onAddMore} disabled={isUploading}>
                        <i className="bi bi-upload me-2"></i>
                        Upload Files
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="modal-footer">
            <div className="d-flex justify-content-between w-100">
              <div>
                {hasChanges && (
                  <small className="text-warning">
                    <i className="bi bi-exclamation-triangle me-1"></i>
                    You have unsaved changes
                  </small>
                )}
              </div>
              <div>
                <button type="button" className="btn btn-secondary me-2" onClick={onClose} disabled={isUploading}>
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleSave}
                  disabled={!hasChanges || isUploading}
                >
                  {isUploading ? (
                    <>
                      <i className="bi bi-hourglass-split me-2"></i>
                      Saving...
                    </>
                  ) : (
                    <>
                      <i className="bi bi-check-circle me-2"></i>
                      Save Changes
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        .border-dashed {
          border: 2px dashed #dee2e6 !important;
        }
        .border-dashed:hover {
          border-color: #0d6efd !important;
          background-color: #f8f9fa;
        }
        .alert-sm {
          padding: 0.25rem 0.5rem;
          font-size: 0.75rem;
        }
      `}</style>
    </div>
  )
}

export default DocumentEditModal
